import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { SalaryType } from "../orders/types/salaryType";
import {
  archiveTesSalaryTable,
  copyTesSalaryTableToDraft,
  createTesSalaryTableDraft,
  deleteTesSalaryTableDraft,
  fetchActiveTesSalaryTables,
  fetchTesSalaryTableById,
  fetchTesSalaryTableConfig,
  fetchTesSalaryTablesByTes,
  publishOrContinueTesSalaryTable,
  publishTesSalaryTable,
  replaceTesSalaryTableDraft,
  setTesSalaryTableConfig,
  estimateTesSalaryTablePublish,
  ActiveTesSalaryTablesResult,
  ArchiveTesSalaryTableParams,
  CopyTesSalaryTableToDraftParams,
  CreateTesSalaryTableDraftParams,
  DeleteTesSalaryTableDraftParams,
  PublishTesSalaryTableParams,
  PublishOrContinueTesSalaryTableParams,
  ReplaceTesSalaryTableDraftParams,
  SetTesSalaryTableConfigParams,
  TesSalaryTableConfig,
  TesSalaryTableCommandResult,
  TesSalaryTableDocument,
  TesSalaryTablePublishCommandResult,
  EstimateTesSalaryTablePublishParams,
  EstimateTesSalaryTablePublishResult,
} from "./tesSalaryTablesApi";

type Status = "idle" | "pending" | "succeeded" | "failed";

type TesSalaryTablesState = {
  tablesByTes: Record<string, TesSalaryTableDocument[]>;
  configByTes: Record<string, TesSalaryTableConfig>;
  tablesStatus: Status;
  configStatus: Status;
};

const initialState: TesSalaryTablesState = {
  tablesByTes: {},
  configByTes: {},
  tablesStatus: "idle",
  configStatus: "idle",
};
const EMPTY_TABLES: TesSalaryTableDocument[] = [];

export const fetchTesSalaryTablesByTesThunk = createAsyncThunk<
  { tesId: string; tables: TesSalaryTableDocument[] },
  string,
  { state: RootState }
>("tesSalaryTables/fetchByTes", async (tesId, { requestId }) => ({
  tesId,
  tables: await fetchTesSalaryTablesByTes(tesId, { requestId }),
}));

export const fetchTesSalaryTableConfigThunk = createAsyncThunk<
  TesSalaryTableConfig,
  string,
  { state: RootState }
>("tesSalaryTables/fetchConfig", async (tesId, { requestId }) =>
  fetchTesSalaryTableConfig(tesId, { requestId }),
);

export const fetchTesSalaryTableByIdThunk = createAsyncThunk<
  TesSalaryTableDocument | null,
  string,
  { state: RootState }
>("tesSalaryTables/fetchById", async (tesSalaryTableId, { requestId }) =>
  fetchTesSalaryTableById(tesSalaryTableId, { requestId }),
);

export const fetchActiveTesSalaryTablesThunk = createAsyncThunk<
  ActiveTesSalaryTablesResult,
  {
    tesId: string;
    date: string;
    workTitleId?: string;
    salaryType?: SalaryType;
  },
  { state: RootState }
>("tesSalaryTables/fetchActive", async (params, { requestId }) =>
  fetchActiveTesSalaryTables(params, { requestId }),
);

export const createTesSalaryTableDraftThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  CreateTesSalaryTableDraftParams,
  { state: RootState }
>("tesSalaryTables/createDraft", async (params, { requestId }) =>
  createTesSalaryTableDraft(params, { requestId }),
);

export const replaceTesSalaryTableDraftThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  ReplaceTesSalaryTableDraftParams,
  { state: RootState }
>("tesSalaryTables/replaceDraft", async (params, { requestId }) =>
  replaceTesSalaryTableDraft(params, { requestId }),
);

export const copyTesSalaryTableToDraftThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  CopyTesSalaryTableToDraftParams,
  { state: RootState }
>("tesSalaryTables/copyToDraft", async (params, { requestId }) =>
  copyTesSalaryTableToDraft(params, { requestId }),
);

export const publishTesSalaryTableThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  PublishTesSalaryTableParams,
  { state: RootState }
>("tesSalaryTables/publish", async (params, { requestId }) =>
  publishTesSalaryTable(params, { requestId }),
);

export const publishOrContinueTesSalaryTableThunk = createAsyncThunk<
  TesSalaryTablePublishCommandResult,
  PublishOrContinueTesSalaryTableParams,
  { state: RootState }
>("tesSalaryTables/publishOrContinue", async (params, { requestId }) =>
  publishOrContinueTesSalaryTable(params, { requestId }),
);

export const estimateTesSalaryTablePublishThunk = createAsyncThunk<
  EstimateTesSalaryTablePublishResult,
  EstimateTesSalaryTablePublishParams,
  { state: RootState }
>("tesSalaryTables/estimatePublish", async (params, { requestId }) =>
  estimateTesSalaryTablePublish(params, { requestId }),
);

export const validateTesSalaryTablePublishThunk = estimateTesSalaryTablePublishThunk;

export const archiveTesSalaryTableThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  ArchiveTesSalaryTableParams,
  { state: RootState }
>("tesSalaryTables/archive", async (params, { requestId }) =>
  archiveTesSalaryTable(params, { requestId }),
);

export const deleteTesSalaryTableDraftThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  DeleteTesSalaryTableDraftParams,
  { state: RootState }
>("tesSalaryTables/deleteDraft", async (params, { requestId }) =>
  deleteTesSalaryTableDraft(params, { requestId }),
);

export const setTesSalaryTableConfigThunk = createAsyncThunk<
  TesSalaryTableCommandResult,
  SetTesSalaryTableConfigParams,
  { state: RootState }
>("tesSalaryTables/setConfig", async (params, { requestId }) =>
  setTesSalaryTableConfig(params, { requestId }),
);

const tesSalaryTablesSlice = createSlice({
  name: "tesSalaryTables",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTesSalaryTablesByTesThunk.pending, (state) => {
      state.tablesStatus = "pending";
    });
    builder.addCase(fetchTesSalaryTablesByTesThunk.rejected, (state) => {
      state.tablesStatus = "failed";
    });
    builder.addCase(fetchTesSalaryTablesByTesThunk.fulfilled, (state, action) => {
      state.tablesByTes[action.payload.tesId] = action.payload.tables;
      state.tablesStatus = "succeeded";
    });
    builder.addCase(fetchTesSalaryTableConfigThunk.pending, (state) => {
      state.configStatus = "pending";
    });
    builder.addCase(fetchTesSalaryTableConfigThunk.rejected, (state) => {
      state.configStatus = "failed";
    });
    builder.addCase(
      fetchTesSalaryTableConfigThunk.fulfilled,
      (state, action) => {
        state.configByTes[action.payload.tesId] = action.payload;
        state.configStatus = "succeeded";
      },
    );
  },
});

export const tesSalaryTablesReducer = tesSalaryTablesSlice.reducer;

export const selectTesSalaryTablesByTes = (state: RootState, tesId?: string | null) =>
  (tesId ? state.tesSalaryTables.tablesByTes[tesId] : undefined) || EMPTY_TABLES;

export const selectTesSalaryTableConfigByTes = (
  state: RootState,
  tesId?: string | null,
) => (tesId ? state.tesSalaryTables.configByTes[tesId] : undefined) || null;
